import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../common/Card';
import Badge from '../common/Badge';
import { useFinance } from '../../context/FinanceContext';
import { TransferStatus, TransferStatusUtils, DetectTransfersResponse, EnrichedTransferPair, GetEnrichedTransferPairsResponse } from '../../types';
import { transferApi } from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';

// Helper functions

const InterAccountTransfers: React.FC = () => {
  const { refreshTransactions } = useFinance();
  const navigate = useNavigate();
  
  // State for backend transfer data
  const [transferPairs, setTransferPairs] = useState<EnrichedTransferPair[]>([]);
  const [transferStats, setTransferStats] = useState<GetEnrichedTransferPairsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [detectingTransfers, setDetectingTransfers] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<TransferStatus | 'ALL'>('ALL');

  /**
   * Load transfer pairs and statistics from backend
   */
  const loadTransferData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load enriched transfer pairs with statistics in single call
      const enrichedResponse = await transferApi.getEnrichedTransferPairs();
      
      setTransferPairs(enrichedResponse.transfers);
      setTransferStats(enrichedResponse);
    } catch (err: any) {
      setError(err.message || 'Failed to load transfer data');
      console.error('Error loading transfer data:', err);
    } finally {
      setLoading(false);
    }
  }, []);


  // Load transfer data on component mount
  useEffect(() => {
    loadTransferData();
  }, [loadTransferData]);

  /**
   * Manually trigger transfer detection
   */
  const handleDetectTransfers = async () => {
    setDetectingTransfers(true);
    setError(null);
    
    try {
      const response: DetectTransfersResponse = await transferApi.detectTransfers();
      
      if (response.success) {
        // Refresh transaction data and reload transfer data
        await refreshTransactions();
        await loadTransferData();
        
        // Show success message
        alert(`✅ Transfer detection completed! Found ${response.transfersDetected} new transfer matches.`);
      } else {
        setError(response.error || 'Failed to detect transfers');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to detect transfers');
      console.error('Error detecting transfers:', err);
    } finally {
      setDetectingTransfers(false);
    }
  };

  /**
   * Approve a transfer pair
   */
  const handleApproveTransfer = async (transferId: string) => {
    try {
      const response = await transferApi.approveTransfer(transferId);
      
      if (response.success) {
        // Update the transfer pair in local state
        setTransferPairs(prev => prev.map(pair => 
          pair.id === transferId 
            ? { ...pair, status: 'APPROVED', reviewedAt: new Date().toISOString() }
            : pair
        ));
        
        // Refresh stats
        await loadTransferData();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to approve transfer');
      console.error('Error approving transfer:', err);
    }
  };

  /**
   * Reject a transfer pair
   */
  const handleRejectTransfer = async (transferId: string) => {
    const reason = prompt('Enter rejection reason (optional):');
    
    try {
      const response = await transferApi.rejectTransfer(transferId, reason || undefined);
      
      if (response.success) {
        // Update the transfer pair in local state
        setTransferPairs(prev => prev.map(pair => 
          pair.id === transferId 
            ? { ...pair, status: 'REJECTED', reviewedAt: new Date().toISOString() }
            : pair
        ));
        
        // Refresh stats
        await loadTransferData();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to reject transfer');
      console.error('Error rejecting transfer:', err);
    }
  };

  /**
   * Filter transfer pairs by status and limit to 3 for dashboard display
   */
  const filteredTransferPairs = useMemo(() => {
    let filtered = transferPairs;
    if (statusFilter !== 'ALL') {
      filtered = transferPairs.filter(pair => pair.status === statusFilter);
    }
    // Limit to 3 items for dashboard display
    return filtered.slice(0, 3);
  }, [transferPairs, statusFilter]);


  /**
   * Check if there are more transfers to show
   */
  const hasMoreTransfers = useMemo(() => {
    let totalCount = transferPairs.length;
    if (statusFilter !== 'ALL') {
      totalCount = transferPairs.filter(pair => pair.status === statusFilter).length;
    }
    return totalCount > 3;
  }, [transferPairs, statusFilter]);

  /**
   * Navigate to full transfers page
   */
  const handleViewAll = () => {
    navigate('/transfers');
  };

  if (loading) {
    return (
      <Card title="Inter-Account Transfers" className="transfer-card">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-3">
            <div className="loading-spinner loading-spinner-lg"></div>
            <span className="text-gray-600">Loading transfers...</span>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Inter-Account Transfers" className="transfer-card">
      {/* Header Controls */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TransferStatus | 'ALL')}
              className="form-control text-sm py-1 px-2"
              style={{ minWidth: '140px' }}
            >
              <option value="ALL">All Statuses</option>
              <option value="UNDER_REVIEW">Under Review</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="NO_MATCH">No Match</option>
            </select>
          </div>
          
          {/* Transfer Statistics */}
          {transferStats && (
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>Under Review: <strong>{transferStats.underReview}</strong></span>
              <span>Approved: <strong>{transferStats.approved}</strong></span>
              <span>Rejected: <strong>{transferStats.rejected}</strong></span>
            </div>
          )}
        </div>

        {/* Detect Transfers Button */}
        <button
          onClick={handleDetectTransfers}
          disabled={detectingTransfers}
          className="btn btn-primary flex items-center space-x-2"
        >
          {detectingTransfers ? (
            <>
              <div className="loading-spinner loading-spinner-sm"></div>
              <span>Detecting...</span>
            </>
          ) : (
            <>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Detect Transfers</span>
            </>
          )}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Pairs Table */}
      {filteredTransferPairs.length === 0 ? (
        <div className="text-center py-12">
          <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No Recent Transfers</h3>
          <p className="text-gray-500 mb-4">
            {statusFilter === 'ALL' 
              ? 'No transfer pairs have been detected yet. Click "Detect Transfers" to find potential matches.'
              : `No recent transfers with status "${TransferStatusUtils.getStatusLabel(statusFilter as TransferStatus)}" found.`
            }
          </p>
          <div className="flex justify-center space-x-3">
            {statusFilter === 'ALL' && (
              <button
                onClick={handleDetectTransfers}
                disabled={detectingTransfers}
                className="btn btn-primary"
              >
                Detect Transfers
              </button>
            )}
            <button
              onClick={handleViewAll}
              className="btn btn-outline-primary"
            >
              View All Transfers
            </button>
          </div>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="data-table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Outgoing Transaction</th>
                <th className="table-header-cell">Income Transaction</th>
                <th className="table-header-cell">Fee</th>
                <th className="table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {filteredTransferPairs.map((pair) => (
                <tr key={pair.id} className="table-row">
                  {/* Status */}
                  <td className="table-cell">
                    <div className="flex flex-col items-start space-y-1">
                      <Badge
                        text={TransferStatusUtils.getStatusLabel(pair.status)}
                        variant={
                          pair.status === 'APPROVED' ? 'success' :
                          pair.status === 'UNDER_REVIEW' ? 'warning' :
                          pair.status === 'REJECTED' ? 'danger' :
                          'secondary'
                        }
                        size="sm"
                      />
                      {pair.status === 'UNDER_REVIEW' && (
                        <div className="text-xs text-blue-600 font-medium">
                          Needs Review
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Outgoing Transaction */}
                  <td className="table-cell">
                    <div>
                      <div className="font-medium text-red-600">
                        {formatCurrency(Math.abs(pair.expenseTransaction.amount), pair.expenseTransaction.currency)}
                      </div>
                      {/* Show converted amount if currencies differ */}
                      {pair.conversionData.isCrossCurrency && (
                        <div className="text-xs text-gray-500">
                          ≈ {formatCurrency(
                            pair.conversionData.expenseAmountInIncomeCurrency,
                            pair.incomeTransaction.currency
                          )}
                        </div>
                      )}
                      <div className="text-sm text-gray-600 truncate" style={{ maxWidth: '200px' }}>
                        {pair.expenseTransaction.description}
                      </div>
                      <div className="text-xs text-gray-500">
                        {pair.expenseTransaction.account.institution} • {pair.expenseTransaction.account.name}
                      </div>
                      <div className="text-xs text-gray-400">
                        {pair.expenseTransaction.timestamp ? formatDate(pair.expenseTransaction.timestamp) : 'No date'}
                      </div>
                    </div>
                  </td>

                  {/* Income Transaction */}
                  <td className="table-cell">
                    <div>
                      <div className="font-medium text-green-600">
                        {formatCurrency(pair.incomeTransaction.amount, pair.incomeTransaction.currency)}
                      </div>
                      <div className="text-sm text-gray-600 truncate" style={{ maxWidth: '200px' }}>
                        {pair.incomeTransaction.description}
                      </div>
                      <div className="text-xs text-gray-500">
                        {pair.incomeTransaction.account.institution} • {pair.incomeTransaction.account.name}
                      </div>
                      <div className="text-xs text-gray-400">
                        {pair.incomeTransaction.timestamp ? formatDate(pair.incomeTransaction.timestamp) : 'No date'}
                      </div>
                    </div>
                  </td>

                  {/* Fee */}
                  <td className="table-cell">
                    <div className={`font-medium ${pair.exchangeFee.amount > 0 ? 'text-red-600' : pair.exchangeFee.amount < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                      {formatCurrency(pair.exchangeFee.amount, pair.exchangeFee.currency)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {pair.conversionData.isCrossCurrency 
                        ? (pair.exchangeFee.amount > 0 ? 'Transfer Cost' : pair.exchangeFee.amount < 0 ? 'Exchange Gain' : 'No Cost')
                        : (pair.exchangeFee.amount > 0 ? 'Fee' : pair.exchangeFee.amount < 0 ? 'Gain' : 'Even')
                      }
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="table-cell">
                    <div className="flex items-center space-x-2">
                      {pair.status === 'UNDER_REVIEW' && (
                        <>
                          <button
                            onClick={() => handleApproveTransfer(pair.id)}
                            className="btn btn-sm btn-success"
                            title="Approve Transfer"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => handleRejectTransfer(pair.id)}
                            className="btn btn-sm btn-danger"
                            title="Reject Transfer"
                          >
                            ✗
                          </button>
                        </>
                      )}
                      {pair.status === 'APPROVED' && (
                        <span className="text-xs text-green-600 font-medium">Approved</span>
                      )}
                      {pair.status === 'REJECTED' && (
                        <span className="text-xs text-red-600 font-medium">Rejected</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* View All Button */}
      {hasMoreTransfers && (
        <div className="mt-4 text-center">
          <button
            onClick={handleViewAll}
            className="btn btn-outline-primary"
          >
            View All Transfers
          </button>
        </div>
      )}

      {/* Summary Statistics */}
      {transferStats && filteredTransferPairs.length > 0 && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{transferStats.underReview}</div>
            <div className="text-sm text-gray-600">Under Review</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{transferStats.approved}</div>
            <div className="text-sm text-gray-600">Approved</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{transferStats.rejected}</div>
            <div className="text-sm text-gray-600">Rejected</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-700">
              {formatCurrency(transferStats.totalFees, transferStats.totalFeesCurrency)}
            </div>
            <div className="text-sm text-gray-600">Total Fees</div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default InterAccountTransfers;